/**
 * Open Source Design Block v1.5
 *
 * Hooks into the variant change events used by the theme (no History API hack,
 * mirrors the pattern from custom-stock-block.js) and swaps the visible STL
 * filename / download URL whenever the customer picks a different variant.
 *
 * - When a variant has no STL: the block hides itself with `--hidden`.
 * - When a variant has an STL: the block reappears with the right file.
 * - Cleans up its listeners on shopify:section:unload (Design Mode safe).
 * - Ignores variant change events for foreign variants (other products' quick-add etc.).
 * - Validates URL scheme before applying to the link's href.
 */
(function () {
  'use strict';

  // Only allow Shopify-CDN-style URLs in the link href: absolute https or protocol-relative.
  function isSafeUrl(url) {
    if (typeof url !== 'string' || url.length === 0) return false;
    if (url.indexOf('https://') === 0) return true;
    if (url.indexOf('//') === 0) return true;
    return false;
  }

  function prettifyFilename(name) {
    if (typeof name !== 'string') return '';
    try {
      return decodeURIComponent(name);
    } catch (_) {
      return name;
    }
  }

  function initBlock(block) {
    if (block.dataset.osdInitialized === 'true') return;
    block.dataset.osdInitialized = 'true';

    let variants = [];
    try {
      variants = JSON.parse(block.dataset.variants || '[]');
    } catch (err) {
      console.warn('Open Source Design: invalid variant data', err);
      return;
    }

    const knownIds = new Set(variants.map((v) => v.id));

    const link = block.querySelector('[data-osd-link]');
    if (!link) return;

    let currentVariantId = parseInt(block.dataset.currentVariantId, 10) || 0;

    function findVariant(id) {
      return variants.find((v) => v.id === id) || null;
    }

    function applyVariant(id) {
      const variant = findVariant(id);
      const url = variant && variant.u;
      const name = variant && variant.n;

      if (url && isSafeUrl(url)) {
        link.setAttribute('href', url);
        link.setAttribute('title', prettifyFilename(name) || '');
        link.removeAttribute('aria-hidden');
        link.removeAttribute('tabindex');
        block.classList.remove('open-source-design--hidden');
      } else {
        link.setAttribute('href', '#');
        link.setAttribute('title', '');
        link.setAttribute('aria-hidden', 'true');
        link.setAttribute('tabindex', '-1');
        block.classList.add('open-source-design--hidden');
      }
    }

    function onVariantChange(id) {
      // Ignore foreign IDs (quick-add cards, other products on the page).
      if (!id || !knownIds.has(id) || id === currentVariantId) return;
      currentVariantId = id;
      block.dataset.currentVariantId = String(id);
      applyVariant(id);
    }

    const cleanups = [];

    // 1. Native theme / Shopify variant events.
    const themeEvents = ['variant:changed', 'variant:change', 'variantChange', 'theme:variant:change'];
    themeEvents.forEach((evtName) => {
      const handler = (e) => {
        const id = (e.detail && (e.detail.variant && e.detail.variant.id || e.detail.id)) || 0;
        if (id) onVariantChange(parseInt(id, 10));
      };
      document.addEventListener(evtName, handler);
      cleanups.push(() => document.removeEventListener(evtName, handler));
    });

    // 2. MutationObserver on the cart form's hidden variant input — most reliable signal.
    const form =
      block.closest('form[action*="/cart/add"]') ||
      document.querySelector('form[action*="/cart/add"]');
    if (form) {
      const input = form.querySelector('input[name="id"]');
      if (input) {
        const observer = new MutationObserver(() => {
          const id = parseInt(input.value, 10);
          if (id) onVariantChange(id);
        });
        observer.observe(input, { attributes: true, attributeFilter: ['value'] });
        cleanups.push(() => observer.disconnect());

        let lastVal = input.value;
        const onChange = () => {
          setTimeout(() => {
            if (input.value !== lastVal) {
              lastVal = input.value;
              const id = parseInt(input.value, 10);
              if (id) onVariantChange(id);
            }
          }, 10);
        };
        form.addEventListener('change', onChange);
        cleanups.push(() => form.removeEventListener('change', onChange));
      }
    }

    // 3. Lightweight URL polling for variant=? query swaps that bypass the input mutation.
    let lastHref = window.location.href;
    const pollId = setInterval(() => {
      if (window.location.href !== lastHref) {
        lastHref = window.location.href;
        const v = new URLSearchParams(window.location.search).get('variant');
        if (v) onVariantChange(parseInt(v, 10));
      }
    }, 250);
    cleanups.push(() => clearInterval(pollId));

    // 4. Browser back/forward — popstate only, never pushState.
    const onPopState = () => {
      const v = new URLSearchParams(window.location.search).get('variant');
      if (v) onVariantChange(parseInt(v, 10));
    };
    window.addEventListener('popstate', onPopState);
    cleanups.push(() => window.removeEventListener('popstate', onPopState));

    // Initial render (covers the case where the server-rendered href / filename are stale).
    applyVariant(currentVariantId);

    block._osdCleanup = () => {
      cleanups.forEach((fn) => {
        try {
          fn();
        } catch (_) {}
      });
    };
  }

  function init() {
    document.querySelectorAll('[data-osd-block]').forEach(initBlock);
  }

  // Page-load init — guarded so multiple snippet renders don't double-register.
  if (!window.__osdPageInitialized) {
    window.__osdPageInitialized = true;
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
    } else {
      init();
    }
  } else {
    // Subsequent executions still need to pick up any newly rendered blocks.
    init();
  }

  // Shopify Design Mode lifecycle — registered exactly once per page even if the
  // script tag executes multiple times via section reloads.
  if (!window.__osdLifecycleRegistered) {
    window.__osdLifecycleRegistered = true;

    document.addEventListener('shopify:section:load', init);

    document.addEventListener('shopify:section:unload', (e) => {
      const root = e.target;
      if (!root || !root.querySelectorAll) return;
      root.querySelectorAll('[data-osd-block]').forEach((block) => {
        if (typeof block._osdCleanup === 'function') block._osdCleanup();
      });
    });
  }
})();
